import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, Loader2, Minus } from 'lucide-react';
import * as React from 'react';
import { cn } from '../cn';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

export const buttonVariants = cva(
  'inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] text-sm font-semibold transition-[background-color,color,box-shadow,transform,filter] duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:brightness-[0.96]',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-foreground/[0.08]',
        outline: 'bg-card text-foreground shadow-[inset_0_0_0_1px_var(--color-input)] hover:bg-muted',
        ghost: 'font-medium text-foreground hover:bg-foreground/[0.06]',
        destructive: 'bg-destructive text-destructive-foreground hover:brightness-[0.95]',
        link: 'rounded-md px-0 text-accent-foreground underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 rounded-lg px-3 text-[13px]',
        md: 'h-10 px-4',
        lg: 'h-11 px-5 text-[15px]',
        icon: 'size-10',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /** Render the child element (e.g. a router Link) with button styles. */
    asChild?: boolean;
    loading?: boolean;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, loading, disabled, children, type, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

const fieldBase =
  'flex w-full rounded-[10px] border border-input bg-card px-3 text-sm text-foreground transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground/80 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/20';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
export const Input = React.forwardRef<HTMLInputElement, InputProps>(({ className, type, ...props }, ref) => (
  <input ref={ref} type={type ?? 'text'} className={cn(fieldBase, 'h-10 py-1', className)} {...props} />
));
Input.displayName = 'Input';

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className, rows, ...props }, ref) => (
  <textarea ref={ref} rows={rows ?? 4} className={cn(fieldBase, 'min-h-20 py-2', className)} {...props} />
));
Textarea.displayName = 'Textarea';

export type SelectOption = { value: string; label: string; disabled?: boolean };
export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  options?: SelectOption[];
  placeholder?: string;
};
/** Styled native select (accessible, mobile friendly). */
/**
 * Classes that size or place the control as a whole (width, margins, flex and grid placement). On a
 * Select they belong on the wrapper, which also positions the chevron: on the inner <select> a
 * `w-28` would shrink the box while the wrapper (and its chevron) stayed full width.
 */
const OUTER_CLASS = /^(?:[a-z0-9-]+:)*-?(?:w-|min-w-|max-w-|m[trblxy]?-|flex-|grow|shrink|basis-|self-|justify-self-|order-|col-|row-)/;

function splitSelectClasses(className: string | undefined): { outer: string; inner: string } {
  const outer: string[] = [];
  const inner: string[] = [];
  for (const token of (className ?? '').split(/\s+/).filter(Boolean)) (OUTER_CLASS.test(token) ? outer : inner).push(token);
  return { outer: outer.join(' '), inner: inner.join(' ') };
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(({ className, options, placeholder, children, ...props }, ref) => {
  const { outer, inner } = splitSelectClasses(className);
  return (
    <div className={cn('relative w-full', outer)}>
      <select ref={ref} className={cn(fieldBase, 'h-10 appearance-none py-1 pr-9', inner)} {...props}>
        {placeholder !== undefined ? (
          <option value="" disabled={props.required}>
            {placeholder}
          </option>
        ) : null}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
    </div>
  );
});
Select.displayName = 'Select';

export const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root ref={ref} className={cn('text-[13px] font-medium leading-none text-foreground/90 peer-disabled:opacity-60', className)} {...props} />
));
Label.displayName = 'Label';

export type CheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & { label?: React.ReactNode };
export const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, label, id, ...props }, ref) => {
    const autoId = React.useId();
    const cid = id ?? autoId;
    const box = (
      <CheckboxPrimitive.Root
        ref={ref}
        id={cid}
        className={cn(
          'peer size-[18px] shrink-0 rounded-[5px] border border-input bg-card transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground',
          className,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center">
          {props.checked === 'indeterminate' ? <Minus className="size-3" /> : <Check className="size-3" />}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
    if (!label) return box;
    return (
      <div className="flex items-center gap-2">
        {box}
        <Label htmlFor={cid} className="font-normal">
          {label}
        </Label>
      </div>
    );
  },
);
Checkbox.displayName = 'Checkbox';

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

export const badgeVariants = cva('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[12px] font-medium leading-5 whitespace-nowrap', {
  variants: {
    variant: {
      default: 'bg-muted text-foreground/80',
      primary: 'bg-accent text-accent-foreground',
      success: 'bg-success/12 text-success',
      warning: 'bg-warning/15 text-accent-foreground',
      destructive: 'bg-destructive/10 text-destructive',
      info: 'bg-info/10 text-info',
      outline: 'shadow-[inset_0_0_0_1px_var(--color-border)] text-muted-foreground',
    },
  },
  defaultVariants: { variant: 'default' },
});
export type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>;
export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('rounded-xl bg-card text-card-foreground shadow-card', className)} {...props} />;
}
export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1 p-6 pb-3', className)} {...props} />;
}
export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-[17px] font-semibold leading-snug tracking-[-0.02em]', className)} {...props} />;
}
export function CardDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}
export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-6 pt-0', className)} {...props} />;
}
export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-center justify-end gap-2 border-t px-6 py-4', className)} {...props} />;
}

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <span role="status" className={cn('inline-flex items-center text-muted-foreground', className)}>
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Centered spinner for page/section loading states. */
export function LoadingState({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div className={cn('flex min-h-40 flex-col items-center justify-center gap-2 text-sm text-muted-foreground', className)}>
      <Spinner label={label} />
      <span>{label}…</span>
    </div>
  );
}

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('animate-pulse rounded-lg bg-foreground/[0.06]', className)} {...props} />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-4 rounded-xl px-6 py-14 text-center', className)}>
      {icon ? <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground [&_svg]:size-6">{icon}</div> : null}
      <div className="space-y-1">
        <h3 className="text-[17px] font-semibold">{title}</h3>
        {description ? <p className="mx-auto max-w-sm text-sm leading-relaxed text-muted-foreground">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  error,
  onRetry,
  className,
}: {
  title?: string;
  error?: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : null;
  return (
    <div role="alert" className={cn('flex flex-col items-center gap-3 rounded-xl bg-destructive/[0.06] p-8 text-center', className)}>
      <h3 className="font-semibold text-destructive">{title}</h3>
      {message ? <p className="max-w-md text-sm text-muted-foreground">{message}</p> : null}
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function Alert({
  variant = 'info',
  title,
  children,
  className,
}: {
  variant?: 'info' | 'success' | 'warning' | 'destructive';
  title?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const styles = {
    info: 'bg-info/[0.07]',
    success: 'bg-success/[0.08]',
    warning: 'bg-accent',
    destructive: 'bg-destructive/[0.07] text-destructive',
  }[variant];
  return (
    <div role={variant === 'destructive' ? 'alert' : 'status'} className={cn('rounded-lg px-4 py-3 text-sm', styles, className)}>
      {title ? <p className="font-medium">{title}</p> : null}
      {children ? <div className={cn(title && 'mt-1', 'text-muted-foreground')}>{children}</div> : null}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?';
}

export function Avatar({
  name,
  src,
  size = 'md',
  className,
}: {
  name: string;
  src?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const [failed, setFailed] = React.useState(false);
  const dims = { xs: 'size-5 text-[10px]', sm: 'size-7 text-xs', md: 'size-9 text-sm', lg: 'size-12 text-base' }[size];
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted font-semibold text-foreground/70', dims, className)}
      title={name}
    >
      {src && !failed ? (
        <img src={src} alt={name} className="size-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden>{initials(name)}</span>
      )}
      <span className="sr-only">{name}</span>
    </span>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  breadcrumbs?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between', className)}>
      <div className="min-w-0 space-y-1.5">
        {breadcrumbs ? <div className="text-[13px] text-muted-foreground">{breadcrumbs}</div> : null}
        <h1 className="truncate text-[30px] font-semibold leading-tight tracking-[-0.03em] sm:text-[36px]">{title}</h1>
        {description ? <p className="text-[15px] text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function Separator({ className, vertical }: { className?: string; vertical?: boolean }) {
  return <div role="separator" className={cn('bg-border', vertical ? 'h-full w-px' : 'h-px w-full', className)} />;
}

export function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className="rounded-md bg-muted px-1.5 py-0.5 font-sans text-[11px] font-medium text-muted-foreground">{children}</kbd>;
}

// ---------------------------------------------------------------------------
// Status marker: the brand hexagon carries status by shape AND tone (filled, ringed, empty), so it
// reads in sunlight and without colour vision. Pair it with a text label (visible or `label`).
// ---------------------------------------------------------------------------

export type StatusTone = 'done' | 'progress' | 'risk' | 'blocked' | 'idle';

const HEX = 'M4.5 2h7L15 8l-3.5 6h-7L1 8z';

export function StatusMarker({
  tone,
  label,
  className,
}: {
  tone: StatusTone;
  /** Accessible name when no visible text sits next to the marker. */
  label?: string;
  className?: string;
}) {
  const a11y = label ? { role: 'img' as const, 'aria-label': label } : { 'aria-hidden': true as const };
  return (
    <svg viewBox="0 0 16 16" className={cn('size-3.5 shrink-0', className)} {...a11y}>
      {tone === 'done' ? <path d={HEX} className="fill-success" /> : null}
      {tone === 'progress' ? (
        <>
          <path d={HEX} fill="none" className="stroke-foreground" strokeWidth={1.6} />
          <path d="M6.2 5h3.6L11.6 8l-1.8 3H6.2L4.4 8z" className="fill-foreground" />
        </>
      ) : null}
      {tone === 'risk' ? (
        <>
          <path d={HEX} className="fill-warning" />
          <path d="M8 5v3.4" stroke="#fff" strokeWidth={1.6} strokeLinecap="round" />
          <circle cx="8" cy="10.8" r="0.9" fill="#fff" />
        </>
      ) : null}
      {tone === 'blocked' ? (
        <>
          <path d={HEX} className="fill-destructive" />
          <path d="M5.2 8h5.6" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
        </>
      ) : null}
      {tone === 'idle' ? <path d={HEX} fill="none" className="stroke-muted-foreground" strokeWidth={1.6} /> : null}
    </svg>
  );
}

/** Marker plus label, the default way to show a status in lists and headers. */
export function Status({ tone, children, className }: { tone: StatusTone; children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[13px] font-medium whitespace-nowrap', className)}>
      <StatusMarker tone={tone} />
      {children}
    </span>
  );
}
